export const socialAnalytics = {
  page: {
    title: 'LinkedIn metrics',
    subtitle:
      'How your team performed on LinkedIn, whether the post went out from Referent or by hand, and who is generating traction.',
    disclosure:
      'Posts published by hand are picked up from each connected account, so they may take a few hours to show up. LinkedIn does not report impressions for every post, so any number with a dotted underline covers part of the period. Unique reach, saves and profile viewers are not available through the API: the Reach column counts impressions.',
    lastSync: {
      fresh: 'Metrics last updated on {{date}}.',
      never: 'Metrics have not been synced yet.',
      stale:
        'Metrics have not been updated since {{date}} — more than {{hours}} hours ago. The sync is probably broken, so these numbers are not standing still on their own.',
    },
  },

  loadError: 'The metrics could not be loaded.',
  retry: 'Try again',

  tabs: {
    competition: 'Competition',
    overTime: 'Over time',
    bestPosts: 'Best posts',
  },

  dateRange: {
    label: 'Date range',
    trigger: '{{from}} - {{to}}',
    presetsLabel: 'Shortcuts',
    presets: {
      thisWeek: 'This week',
      last14Days: 'Last 14 days',
      last30Days: 'Last 30 days',
      quarterToDate: 'Quarter to date',
      last3Months: 'Last 3 months',
      last6Months: 'Last 6 months',
      thisYear: 'This year',
      allTime: 'All time',
      custom: 'Custom',
    },
  },

  coverage: {
    noData: 'No data',
    noDataHint: 'LinkedIn did not report this metric for any post in the period.',
    partialHint: '{{reported}} of {{published}} posts reported this metric.',
  },

  kpi: {
    publishedPosts: 'Published posts',
    reactions: 'Reactions',
    comments: 'Comments',
    impressions: 'Impressions',
    engagementRate: 'Engagement rate',
    deltaVsPrevious: '{{value}} vs. previous period',
    ratePoints: '{{value}} pts',
    noComparison: 'No comparison available',
  },

  awaitingSync: {
    title: 'Metrics have not arrived yet',
    description:
      'These posts were published, but no capture has landed on them yet. Metrics are refreshed every {{minHours}}-{{maxHours}} hours per account, so a post published just now has no numbers until the next run.',
  },

  empty: {
    noPosts: {
      title: 'No posts published yet',
      description:
        'Publish a post to LinkedIn — from Referent or by hand — and its metrics will show up here after the next sync.',
    },
    noPostsInPeriod: {
      title: 'Nothing published in this period',
      description: 'There was activity before this window. Widen the period to see it.',
    },
    notConnected: {
      title: 'Connect your LinkedIn account',
      description:
        'These metrics are read from the LinkedIn account you connect to Referent. Connect yours and every post — published from Referent or by hand — starts reporting here after the next sync.',
    },
    needsReconnect: {
      title: 'Your LinkedIn connection stopped working',
      description:
        'Referent can no longer read your LinkedIn account, so nothing is being captured from it. Reconnect it to resume the sync.',
    },
  },

  posts: {
    unknownAuthor: 'Unknown author',
    openOnLinkedIn: 'Open on LinkedIn',
    reactionsBreakdownLabel: 'Reactions by type',
    origin: {
      filter: {
        label: 'Origin',
        all: 'All',
      },
      referent: {
        label: 'Referent',
        hint: 'Published from Referent.',
      },
      external: {
        label: 'Outside Referent',
        hint: 'Published by hand on LinkedIn and picked up from the connected account.',
      },
    },
    columns: {
      engagementRate: 'Engagement',
    },
  },

  reactions: {
    like: 'Like',
    praise: 'Celebrate',
    appreciation: 'Support',
    empathy: 'Love',
    interest: 'Insightful',
    entertainment: 'Funny',
    maybe: 'Curious',
  },

  detail: {
    title: 'Post detail',
    description: 'How this post accumulated engagement across every capture.',
    loadError: 'The history could not be loaded.',
    retry: 'Try again',
    metricSelectLabel: 'Metric to chart',
    deletedOnLinkedIn:
      'This post no longer exists on LinkedIn (detected on {{date}}). Its history is kept, but it no longer counts towards the KPIs.',
    lastSyncedAt: 'Last updated on {{date}}',
    metrics: {
      reactions: 'Reactions',
      comments: 'Comments',
      reposts: 'Reposts',
      impressions: 'Impressions',
      pageViewers: 'Profile views from this post',
      followersGained: 'Followers gained',
    },
    empty: {
      noCaptures: {
        title: 'No captures yet',
        description:
          'Metrics are refreshed every {{minHours}}-{{maxHours}} hours per account. This post has not been captured yet.',
      },
      singleCapture: {
        title: 'Only one capture so far',
        description:
          'A chart needs at least two captures to show movement. Metrics accumulate over the days, with a refresh every {{minHours}}-{{maxHours}} hours per account.',
      },
      thinMetric: {
        title: 'Not enough captures for this metric',
        description:
          'This post has history, but this metric was only reported once. Pick another metric or come back after the next sync.',
      },
    },
  },

  competition: {
    rank: '#{{rank}}',
    scoreLabel: 'Score',
    loadError: 'The standings could not be loaded.',
    rankDelta: {
      up: 'Up {{count}} positions vs. the previous period',
      down: 'Down {{count}} positions vs. the previous period',
    },
    change: {
      vsPrevious: '{{value}} vs. the previous period',
    },
    podium: {
      title: 'Podium',
      subtitle: 'The three highest scores of the period.',
      participants: '{{count}} participants',
      emptySlot: 'Nobody reached position {{rank}} in this period.',
    },
    table: {
      title: 'Leaderboard',
      subtitle: 'Everyone who published in the period, with the metrics their score came from.',
      empty: 'Nobody published in this period.',
      total: 'Total',
      averageScore: 'Average',
    },
    columns: {
      rank: 'Rank',
      collaborator: 'Collaborator',
      score: 'Score',
      reach: 'Reach',
      engagement: 'Engagement',
      posts: 'Posts',
      comments: 'Comments',
      followers: 'Followers',
    },
    empty: {
      title: 'Nobody published in this period',
      description:
        'The standings fill in as the people in your organization publish on LinkedIn. Widen the period to see earlier activity.',
    },
    scoreHelp: {
      trigger: 'How is the score calculated?',
      title: 'How the score is calculated',
      steps: {
        normalize:
          'Each metric is scaled from 0 to 100 against the best and the worst of the period (min-max).',
        weight: 'Each dimension counts for its own weight:',
        total: 'The weighted sum, rounded, is the 0-100 score.',
      },
      dimensions: {
        impressions: 'Reach',
        reactions: 'Reactions',
        followersGained: 'Followers gained',
        posts: 'Posts',
        comments: 'Comments',
      },
      engagementNote: 'Engagement rate does not score: it is shown for reading only.',
      relativeNote:
        'The score is relative to the period: changing the date range recalculates the whole board.',
    },
  },

  overTime: {
    loadError: 'The evolution over time could not be loaded.',
    bucket: {
      weekOf: 'Week of {{date}}',
    },
    gap: {
      noPosts: 'Nothing was published',
      notReported: 'No post reported this metric',
    },
    seriesNote: {
      daily:
        'Each point adds up what the posts published that day have accumulated since — not the interactions that happened on it.',
      weekly:
        'Each point adds up what the posts published that week have accumulated since — not the interactions that happened in it.',
    },
    gapNote:
      'A break in a line is not a zero: it means no post reported that metric in that stretch. Published posts are the exception — Referent counts them itself, so a zero there really does mean nothing went out.',
    empty: {
      title: 'There is nothing to chart in this period',
      description: 'Widen the date range and the evolution of the period shows up here.',
    },
  },

  bestPosts: {
    noText: 'Post with no text',
    loadError: 'The post ranking could not be loaded.',
    empty: {
      title: 'No posts to rank in this period',
      description:
        'The ranking fills in as your team publishes on LinkedIn. Widen the period to see earlier activity.',
    },
    podium: {
      title: 'Top posts',
      subtitle: 'The three highest-scoring posts of the period.',
      emptySlot: 'No post reached position {{rank}} in this period.',
      openPost: 'Open the #{{rank}} post, by {{author}}, on LinkedIn',
    },
    table: {
      title: 'Post ranking',
      subtitle: 'Every post of the period, scored against the best one of the set.',
      empty: 'No posts were published in this period.',
      emptyForOrigin: 'No post of this origin was published in the period.',
      truncated:
        'The ranking covers the {{shown}} highest-scoring posts of the period, out of {{total}} published.',
      originNote:
        'Filtering by origin only hides rows: the rank and the score still belong to the whole period.',
      openDetail: 'See the detail of the post by {{author}}',
    },
    columns: {
      rank: 'Rank',
      author: 'Author',
      score: 'Score',
      content: 'Content',
      impressions: 'Impressions',
      reactions: 'Reactions',
      comments: 'Comments',
      reposts: 'Reposts',
      publishedAt: 'Published',
      actions: 'Actions',
    },
  },
} as const
